import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import { useGlobalContext } from '../contexts/GlobalContext';
import {
  getAllPages,
  getMonitorGroups,
  saveMonitorGroup,
  deleteMonitorGroup,
  getMonitorGroupFields,
  saveMonitorGroupField,
  deleteMonitorGroupField,
} from '../services/api';

const label = entity => {
  if (!entity) return '';
  const name = entity.name || entity.summary?.name || entity.entity_model_name;
  const mapName = entity.map_name || entity.summary?.map_name;
  return `${name} · Entity #${entity.id}${name !== entity.entity_model_name ? ` · ${entity.entity_model_name}` : ''}${mapName ? ` · ${mapName}` : ' · Unmapped'}`;
};
const errorMessage = error => {
  const data = error.response?.data;
  if (!data) return 'Unable to complete the request. Please try again.';
  if (data.affected_groups)
    return `${data.detail} Affected groups: ${data.affected_groups.map(g => g.name).join(', ')}.`;
  return Object.entries(data)
    .map(
      ([key, value]) =>
        `${key === 'detail' ? '' : `${key}: `}${Array.isArray(value) ? value.join(', ') : typeof value === 'object' ? JSON.stringify(value) : value}`
    )
    .join(' ');
};
const emptyField = {
  key: '',
  label: '',
  field_type: 'string',
  enumText: '',
  required: false,
  order: 0,
};

export default function MonitorGroupsPage({ user, onLogout }) {
  const [params] = useSearchParams();
  const [organizations, setOrganizations] = useState([]);
  const { selectedOrganization, setSelectedOrganization } = useGlobalContext();
  const org = selectedOrganization?.id || '';
  const currentOrganization = useRef(selectedOrganization);
  currentOrganization.current = selectedOrganization;
  const [groups, setGroups] = useState([]);
  const [entities, setEntities] = useState([]);
  const [models, setModels] = useState([]);
  const [fields, setFields] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [draft, setDraft] = useState(null);
  const [fieldEditor, setFieldEditor] = useState(false);
  const [fieldDraft, setFieldDraft] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const generation = useRef(0);
  const openedLink = useRef('');

  useEffect(() => {
    let active = true;
    getAllPages('/organizations/')
      .then(items => {
        if (!active) return;
        setOrganizations(items);
        const requested = Number(params.get('organization'));
        setSelectedOrganization(
          items.find(o => o.id === requested) ||
            items.find(o => o.id === currentOrganization.current?.id) ||
            items[0] ||
            null
        );
        if (!items.length) setLoading(false);
      })
      .catch(e => {
        if (active) {
          setError(errorMessage(e));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const reload = async organization => {
    const current = ++generation.current;
    setLoading(true);
    try {
      const [nextGroups, nextEntities, nextFields, nextModels] =
        await Promise.all([
          getMonitorGroups(organization),
          getAllPages('/entities/', { organization }),
          getMonitorGroupFields(organization),
          getAllPages('/entity-models/'),
        ]);
      if (current !== generation.current) return;
      setGroups(nextGroups);
      setEntities(nextEntities);
      setFields(nextFields);
      setModels(
        nextModels.filter(m => Number(m.organization) === Number(organization))
      );
      const linkedId = params.get('group');
      if (linkedId && openedLink.current !== linkedId) {
        const group = nextGroups.find(g => String(g.id) === linkedId);
        if (group) {
          openGroup(group);
          openedLink.current = linkedId;
        }
      }
    } catch (e) {
      if (current === generation.current) setError(errorMessage(e));
    } finally {
      if (current === generation.current) setLoading(false);
    }
  };
  useEffect(() => {
    setDraft(null);
    setFieldEditor(false);
    setFieldDraft(null);
    setDeleting(null);
    setError('');
    setGroups([]);
    setEntities([]);
    setModels([]);
    setFields([]);
    if (org) reload(org);
    return () => {
      generation.current++;
    };
  }, [org]);

  function openGroup(group) {
    setDialogError('');
    setDraft(
      group
        ? {
            id: group.id,
            name: group.name,
            master_entity_id: group.master?.id || null,
            secondary_entity_ids: group.secondaries.map(e => e.id),
            assigned_star_entity_id: group.assigned_star_entity_id,
            values: { ...group.values },
          }
        : {
            name: '',
            master_entity_id: null,
            secondary_entity_ids: [],
            assigned_star_entity_id: null,
            values: {},
          }
    );
  }
  const members = entities.filter(
    e => !e.monitor_group || e.monitor_group.id === draft?.id
  );
  const stars = entities.filter(e =>
    models.some(m => m.id === e.entity_model && m.assignable_as_star)
  );
  const selectEntity = (key, title, options) => (
    <Autocomplete
      options={options}
      getOptionLabel={label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={entities.find(e => e.id === draft[key]) || null}
      onChange={(_, value) => setDraft({ ...draft, [key]: value?.id || null })}
      renderInput={p => <TextField {...p} label={title} required />}
    />
  );
  const saveGroup = async () => {
    setSaving(true);
    setDialogError('');
    try {
      await saveMonitorGroup(draft.id, {
        ...draft,
        organization: org,
        values: {
          ...Object.fromEntries(
            fields
              .filter(f => f.required && f.field_type === 'boolean')
              .map(f => [f.key, false])
          ),
          ...draft.values,
        },
      });
      setDraft(null);
      await reload(org);
    } catch (e) {
      setDialogError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  const saveField = async () => {
    setSaving(true);
    setDialogError('');
    const { enumText, id, ...data } = fieldDraft;
    try {
      await saveMonitorGroupField(id, {
        ...data,
        organization: org,
        enum: enumText
          .split('\n')
          .map(x => x.trim())
          .filter(Boolean),
      });
      setFieldDraft(null);
      await reload(org);
    } catch (e) {
      setDialogError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  const confirmDelete = async () => {
    setSaving(true);
    setDialogError('');
    try {
      if (deleting.type === 'group') await deleteMonitorGroup(deleting.id);
      else await deleteMonitorGroupField(deleting.id);
      setDeleting(null);
      await reload(org);
    } catch (e) {
      setDialogError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout user={user} onLogout={onLogout}>
      <Stack spacing={2} sx={{ width: '100%' }}>
        <Typography variant="h4">Monitor Groups</Typography>
        <Typography color="text.secondary">
          Organize devices under one master and assign a star to the group.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Organization"
            disabled={saving}
            value={org}
            onChange={e =>
              setSelectedOrganization(
                organizations.find(o => o.id === e.target.value) || null
              )
            }
            sx={{ minWidth: 220 }}
          >
            {organizations.map(o => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Search groups"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Button
            variant="contained"
            disabled={!org || loading}
            onClick={() => openGroup(null)}
          >
            New group
          </Button>
          <Button
            disabled={!org || loading}
            onClick={() => {
              setFieldEditor(true);
              setDialogError('');
            }}
          >
            Group fields
          </Button>
        </Stack>
        {loading ? (
          <CircularProgress aria-label="Loading monitor groups" />
        ) : !org ? (
          <Alert severity="info">
            Create or join an organization to manage monitor groups.
          </Alert>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    'Name',
                    'Master',
                    'Secondaries',
                    'Assigned star',
                    'Actions',
                  ].map(h => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groups
                  .filter(g =>
                    g.name.toLowerCase().includes(search.toLowerCase())
                  )
                  .map(g => (
                    <TableRow key={g.id}>
                      <TableCell>{g.name}</TableCell>
                      <TableCell>{label(g.master)}</TableCell>
                      <TableCell>{g.secondaries.length}</TableCell>
                      <TableCell>{label(g.assigned_star)}</TableCell>
                      <TableCell>
                        <Button onClick={() => openGroup(g)}>Edit</Button>
                        <Button
                          color="error"
                          onClick={() => {
                            setDialogError('');
                            setDeleting({
                              type: 'group',
                              id: g.id,
                              name: g.name,
                            });
                          }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {!groups.length && (
              <Typography sx={{ p: 2 }}>
                No monitor groups yet. Create a group to get started.
              </Typography>
            )}
          </Box>
        )}
      </Stack>
      <Dialog
        open={!!draft}
        onClose={() => !saving && setDraft(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {draft?.id ? 'Edit monitor group' : 'New monitor group'}
        </DialogTitle>
        <DialogContent>
          {draft && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {dialogError && <Alert severity="error">{dialogError}</Alert>}
              <TextField
                label="Name"
                required
                value={draft.name}
                onChange={e => setDraft({ ...draft, name: e.target.value })}
              />
              {selectEntity(
                'master_entity_id',
                'Master',
                members.filter(e => !draft.secondary_entity_ids.includes(e.id))
              )}
              <Autocomplete
                multiple
                options={members.filter(e => e.id !== draft.master_entity_id)}
                getOptionLabel={label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={entities.filter(e =>
                  draft.secondary_entity_ids.includes(e.id)
                )}
                onChange={(_, values) =>
                  setDraft({
                    ...draft,
                    secondary_entity_ids: values.map(e => e.id),
                  })
                }
                renderInput={p => <TextField {...p} label="Secondaries" />}
              />
              <Typography variant="body2" color="text.secondary">
                Devices in another group are unavailable. Edit that group to
                remove or replace them first. To promote a secondary, remove it
                from this list, then choose it as master before saving.
              </Typography>
              {selectEntity('assigned_star_entity_id', 'Assigned star', stars)}
              {!stars.length && (
                <Alert severity="info">
                  No eligible stars. In Entity Models, enable “Assignable as
                  star” for the appropriate model, then create a device using
                  it.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                The star can serve multiple groups. Changing the master keeps
                this star assignment.
              </Typography>
              {fields.map(field => {
                const value = draft.values[field.key] ?? '';
                const change = next =>
                  setDraft({
                    ...draft,
                    values: { ...draft.values, [field.key]: next },
                  });
                const remove = () => {
                  const values = { ...draft.values };
                  delete values[field.key];
                  setDraft({ ...draft, values });
                };
                return (
                  <Box key={field.id}>
                    {field.field_type === 'boolean' ? (
                      <FormControlLabel
                        label={`${field.label}${field.required ? ' *' : ''}`}
                        control={
                          <Checkbox
                            checked={value === true}
                            onChange={e => change(e.target.checked)}
                          />
                        }
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label={field.label}
                        required={field.required}
                        value={value}
                        select={field.enum.length > 0}
                        type={
                          ['integer', 'float'].includes(field.field_type)
                            ? 'number'
                            : 'text'
                        }
                        multiline={field.field_type === 'text'}
                        minRows={field.field_type === 'text' ? 2 : undefined}
                        onChange={e => change(e.target.value)}
                      >
                        {field.enum.length > 0
                          ? [
                              <MenuItem key="empty" value="">
                                —
                              </MenuItem>,
                              ...field.enum.map(option => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              )),
                            ]
                          : null}
                      </TextField>
                    )}
                    {!field.required &&
                      Object.hasOwn(draft.values, field.key) && (
                        <Button size="small" onClick={remove}>
                          Clear {field.label}
                        </Button>
                      )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setDraft(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              saving ||
              !draft?.name.trim() ||
              !draft?.master_entity_id ||
              !draft?.assigned_star_entity_id
            }
            onClick={saveGroup}
          >
            {saving ? 'Saving…' : 'Save group'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={fieldEditor}
        onClose={() => !saving && setFieldEditor(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Group fields</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            These properties apply to every monitor group in this organization.
            Keys stay fixed after creation. Changes must remain compatible with
            existing groups.
          </Typography>
          <Stack spacing={1}>
            {fields.map(f => (
              <Stack key={f.id} direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ flex: 1 }}>
                  {f.label} <Chip size="small" label={f.field_type} />
                  {f.required ? ' (required)' : ''}
                </Typography>
                <Button
                  onClick={() => {
                    setDialogError('');
                    setFieldDraft({ ...f, enumText: f.enum.join('\n') });
                  }}
                >
                  Edit
                </Button>
                <Button
                  color="error"
                  onClick={() => {
                    setDialogError('');
                    setDeleting({ type: 'field', id: f.id, name: f.label });
                  }}
                >
                  Delete
                </Button>
              </Stack>
            ))}
          </Stack>
          {!fields.length && (
            <Typography>No custom fields configured.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldEditor(false)}>Close</Button>
          <Button
            onClick={() => {
              setDialogError('');
              setFieldDraft({ ...emptyField, order: fields.length });
            }}
          >
            Add field
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!fieldDraft}
        onClose={() => !saving && setFieldDraft(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{fieldDraft?.id ? 'Edit field' : 'Add field'}</DialogTitle>
        <DialogContent>
          {fieldDraft && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {dialogError && <Alert severity="error">{dialogError}</Alert>}
              <TextField
                label="Label"
                required
                value={fieldDraft.label}
                onChange={e =>
                  setFieldDraft({ ...fieldDraft, label: e.target.value })
                }
              />
              <TextField
                label="Key"
                required
                disabled={!!fieldDraft.id}
                helperText="Letters, numbers, underscores, and hyphens. Cannot change after saving."
                value={fieldDraft.key}
                onChange={e =>
                  setFieldDraft({ ...fieldDraft, key: e.target.value })
                }
              />
              <TextField
                select
                label="Type"
                value={fieldDraft.field_type}
                onChange={e =>
                  setFieldDraft({
                    ...fieldDraft,
                    field_type: e.target.value,
                    enumText: '',
                  })
                }
              >
                {['string', 'text', 'integer', 'float', 'boolean'].map(t => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
              {fieldDraft.field_type === 'string' && (
                <TextField
                  label="Dropdown options (one per line)"
                  multiline
                  minRows={3}
                  helperText="Leave empty for free text."
                  value={fieldDraft.enumText}
                  onChange={e =>
                    setFieldDraft({ ...fieldDraft, enumText: e.target.value })
                  }
                />
              )}
              <TextField
                label="Order"
                type="number"
                value={fieldDraft.order}
                onChange={e =>
                  setFieldDraft({ ...fieldDraft, order: e.target.value })
                }
              />
              <FormControlLabel
                label="Required"
                control={
                  <Checkbox
                    checked={fieldDraft.required}
                    onChange={e =>
                      setFieldDraft({
                        ...fieldDraft,
                        required: e.target.checked,
                      })
                    }
                  />
                }
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setFieldDraft(null)}>
            Cancel
          </Button>
          <Button
            disabled={
              saving || !fieldDraft?.key.trim() || !fieldDraft?.label.trim()
            }
            onClick={saveField}
          >
            Save field
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!deleting} onClose={() => !saving && setDeleting(null)}>
        <DialogTitle>Delete {deleting?.name}?</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error">{dialogError}</Alert>}
          <Typography>
            {deleting?.type === 'group'
              ? 'This removes the group and releases its members. Devices are preserved.'
              : 'Clear this optional property from existing groups before deleting its field. If required, make it optional first.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button color="error" disabled={saving} onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
